import { AuthApiError, authRequest } from '../../../core/api/authApiClient';
import { setAccessToken } from '../../../core/api/tokenStorage';
import i18n from '../../../i18n';
import type {
  AuthRepository,
  CheckEmailCommand,
  CheckEmailResponse,
  SignInCommand,
  SignUpCommand,
  SignUpResponse,
  SnsLinkCommand,
  SnsSelectCommand,
  SnsSignInCommand,
  SnsSignUpCommand,
} from '../Domain/repository/AuthRepository';
import type { AuthSession, AuthUserProfile, SnsSignInResult } from '../Domain/entity/AuthTypes';

import { formatUserFullName } from '../../../Utils/formatUserName';

const deriveName = (p: AuthUserProfile): string | undefined => {
  const full = formatUserFullName(p.first_name, p.last_name);
  return full || p.email;
};

// /signin 과 같은 형식의 응답(프로필 + token) → 세션
const toSession = (res: any): AuthSession => {
  const profile = res as AuthUserProfile;

  const accessToken = String(res?.token?.accessToken ?? '');
  setAccessToken(accessToken);

  return {
    accessToken,
    issuedAt: profile.updated_at ?? profile.created_at,
    user: {
      ...profile,
      id: profile.user_id,
      name: deriveName(profile),
    },
  };
};

/**
 * SNS 로그인 API 오류 → Error
 * - 화면에서 result_Code 로 안내 문구를 고를 수 있게 message 를 `SNS_{result_Code}` 로 만든다
 * - 가입 실패(40050)는 서버 메시지(Duplicated Email 등)를 뒤에 붙인다
 */
const toSnsError = (error: unknown): Error => {
  if (error instanceof AuthApiError) {
    const body = (error.body ?? {}) as { result_Code?: number; result_message?: unknown };
    if (body.result_Code !== undefined) {
      const detail =
        body.result_Code === 40050 && typeof body.result_message === 'string' ? `:${body.result_message}` : '';
      return new Error(`SNS_${body.result_Code}${detail}`);
    }
  }
  return error instanceof Error ? error : new Error(String(error));
};

const snsSessionRequest = async (path: string, body: unknown): Promise<AuthSession> => {
  try {
    const res = await authRequest<any>(path, { body, withAccessToken: false });
    if (!res?.user_id || !res?.token?.accessToken) {
      throw new Error(i18n.t('common.repositoryErrors.authSignInFailed'));
    }
    return toSession(res);
  } catch (error) {
    throw toSnsError(error);
  }
};

export class AuthRepositoryImpl implements AuthRepository {
  // POST /signin: 모금자 로그인
  async signIn(command: SignInCommand): Promise<AuthSession> {
    const res = await authRequest<any>('/signin', {
      body: {
        user_email: command.user_email,
        password: command.password,
        partner: command.partner,
        device_id: command.device_id,
        mobile_os_type: command.mobile_os_type,
        authentication_by: command.authentication_by,
        access_at: command.access_at,
        user_agent: command.user_agent,
      },
    });

    // result_Code 또는 statusCode 확인 (0 또는 200이 아니면 실패)
    if (res.result_Code !== undefined && res.result_Code !== 200 && res.result_Code !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : res.result_message?.message || JSON.stringify(res.result_message) || i18n.t('common.repositoryErrors.authSignInFailed');
      throw new Error(msg);
    }
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : res.result_message?.message || JSON.stringify(res.result_message) || i18n.t('common.repositoryErrors.authSignInFailed');
      throw new Error(msg);
    }

    return toSession(res);
  }

  // POST /auth/user/{provider}/signin: SNS 인증 → 연결된 계정 목록 + 티켓
  async snsSignIn(command: SnsSignInCommand): Promise<SnsSignInResult> {
    try {
      const res = await authRequest<any>(`/auth/user/${command.provider}/signin`, {
        body: {
          code: command.code,
          redirect_uri: command.redirect_uri,
          partner: command.partner,
        },
        withAccessToken: false,
      });
      if (!res?.ticket) {
        throw new Error(i18n.t('common.repositoryErrors.authSignInFailed'));
      }
      return {
        ticket: String(res.ticket),
        accounts: Array.isArray(res.accounts) ? res.accounts : [],
        profile: {
          email: String(res.profile?.email ?? ''),
          name: String(res.profile?.name ?? ''),
          phone: String(res.profile?.phone ?? ''),
        },
        gid: typeof res.gid === 'string' && res.gid ? res.gid : null,
        isNewUser: res.is_new_user === true,
      };
    } catch (error) {
      throw toSnsError(error);
    }
  }

  // POST /auth/user/sns/select: 연결된 계정으로 로그인
  async snsSelect(command: SnsSelectCommand): Promise<AuthSession> {
    return snsSessionRequest('/auth/user/sns/select', command);
  }

  // POST /auth/user/sns/link: 이메일+비밀번호 로그인 → SNS 연결 → 로그인
  async snsLink(command: SnsLinkCommand): Promise<AuthSession> {
    return snsSessionRequest('/auth/user/sns/link', command);
  }

  // POST /auth/user/sns/signup: SNS 인증 정보로 회원가입 → 로그인
  async snsSignUp(command: SnsSignUpCommand): Promise<AuthSession> {
    return snsSessionRequest('/auth/user/sns/signup', command);
  }

  // POST /users: 회원가입(사용자 생성)
  async signUp(command: SignUpCommand): Promise<SignUpResponse> {
    const res = await authRequest<SignUpResponse>('/users', { body: command });
    
    // result_Code 또는 statusCode 확인 (0 또는 200이 아니면 실패)
    if (res.result_Code !== undefined && res.result_Code !== 200 && res.result_Code !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || i18n.t('common.repositoryErrors.authSignUpFailed');
      throw new Error(msg);
    }
    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      const msg = typeof res.result_message === 'string' 
        ? res.result_message 
        : (res as any).result_message?.message || JSON.stringify(res.result_message) || i18n.t('common.repositoryErrors.authSignUpFailed');
      throw new Error(msg);
    }

    return res;
  }

  // POST /users/checkemail: 이메일 중복 확인
  async checkEmail(command: CheckEmailCommand): Promise<CheckEmailResponse> {
    try {
      const res = await authRequest<Omit<CheckEmailResponse, 'available'>>('/users/checkemail', {
        body: { email: command.email },
      });

      const resultCode = res.result_Code;
      const statusCode = res.statusCode;
      const isSuccessResultCode = resultCode === undefined || resultCode === 0 || resultCode === 200;
      const isSuccessStatusCode = statusCode === undefined || statusCode === 0 || statusCode === 200;

      // API가 HTTP 200으로 내려도 payload 코드가 400이면 중복으로 처리
      if (resultCode === 400 || statusCode === 400) {
        return {
          ...res,
          available: false,
        };
      }

      if (!isSuccessResultCode || !isSuccessStatusCode) {
        const msg =
          typeof res.result_message === 'string'
            ? res.result_message
            : i18n.t('common.repositoryErrors.checkEmailFailed', { code: resultCode ?? statusCode ?? 'unknown' });
        throw new Error(msg);
      }

      return {
        ...res,
        available: true,
      };
    } catch (error) {
      if (error instanceof AuthApiError && error.status === 400) {
        const errorBody = (error.body ?? {}) as {
          statusCode?: number;
          result_Code?: number;
          result_message?: string;
          [key: string]: unknown;
        };

        return {
          ...errorBody,
          available: false,
        };
      }

      throw error;
    }
  }
}

